import { Component, OnInit } from '@angular/core';
import { DictionaryEntryModel, DictionaryKey, DictionaryModel, HxDictionaryService, HxUiLabelPipe, toRouterQueryParams, uiLabel } from 'hx-services';
import { ActivatedRoute, Data, Router } from '@angular/router';
import { TranslocoDirective, TranslocoPipe, TranslocoService } from '@jsverse/transloco';
import { NzNotificationService } from 'ng-zorro-antd/notification';
import { takeUntil } from 'rxjs/operators';
import { NzModalService } from 'ng-zorro-antd/modal';
import { AddEntryModal } from '@adm-web-app/modules/dictionaries/modal/add-entry/add-entry.modal';
import { firstValueFrom, Subject } from 'rxjs';
import { NzAffixComponent } from 'ng-zorro-antd/affix';
import { NzPageHeaderComponent, NzPageHeaderExtraDirective, NzPageHeaderSubtitleDirective, NzPageHeaderTitleDirective } from 'ng-zorro-antd/page-header';
import { NzButtonComponent } from 'ng-zorro-antd/button';
import { NzWaveDirective } from 'ng-zorro-antd/core/wave';

import { NzCollapseComponent, NzCollapsePanelComponent } from 'ng-zorro-antd/collapse';
import { NzColDirective, NzRowDirective } from 'ng-zorro-antd/grid';
import { NzInputDirective, NzInputGroupComponent } from 'ng-zorro-antd/input';
import { FormsModule } from '@angular/forms';
import { NzSwitchComponent } from 'ng-zorro-antd/switch';
import {
  NzTableCellDirective,
  NzTableComponent,
  NzTbodyComponent,
  NzTdAddOnComponent,
  NzThAddOnComponent,
  NzTheadComponent,
  NzThMeasureDirective,
  NzTrDirective
} from 'ng-zorro-antd/table';
import { JsonPipe, NgClass } from '@angular/common';
import { NzInputNumberComponent } from 'ng-zorro-antd/input-number';
import { HxLabelEditComponent } from 'hx-components';
import { NzIconDirective } from 'ng-zorro-antd/icon';
import Timeout = NodeJS.Timeout;

export interface TreeNodeInterface extends DictionaryEntryModel {
  level?: number;
  expand?: boolean;
  parent?: TreeNodeInterface;
  removed?: boolean;
}

interface DictionaryEntryItem extends DictionaryEntryModel {
  removed?: boolean;
}

@Component({
    selector: 'hc-entry-list',
    templateUrl: './entry-list.component.html',
    styleUrls: ['./entry-list.component.less'],
  imports: [TranslocoDirective, NzAffixComponent, NzPageHeaderComponent, NzPageHeaderTitleDirective, NzPageHeaderSubtitleDirective, NzPageHeaderExtraDirective, NzButtonComponent, NzWaveDirective, NzCollapseComponent, NzCollapsePanelComponent, NzRowDirective, NzColDirective, NzInputDirective, FormsModule, NzSwitchComponent, NzTableComponent, NzTheadComponent, NzTrDirective, NzTableCellDirective, NzThMeasureDirective, NzThAddOnComponent, NzTbodyComponent, NgClass, NzTdAddOnComponent, NzInputNumberComponent, HxLabelEditComponent, NzInputGroupComponent, NzIconDirective, JsonPipe, HxUiLabelPipe, TranslocoPipe]
})
export class DictionaryEntryListComponent  implements OnInit {
  mapOfExpandedData: { [key: string]: TreeNodeInterface[] } = {};
  params: { query?: string } = {};
  dictionary!: DictionaryModel;
  entries: DictionaryEntryItem[] = [];
  expandAll = false;
  editIx?: number;
  dirty = false;
  isLoading = {
    saveEntries: false,
  };
  tooltipTitle?: string;
  private expandedEntries: { [key: string]: boolean } = {};
  private entryMap: Map<number, DictionaryEntryItem> = new Map();
  private timeoutId?: Timeout;
  private $destroyed = new Subject<void>();

  constructor(
    private router: Router,
    private aRoute: ActivatedRoute,
    private nt: NzNotificationService,
    private tr: TranslocoService,
    private modal: NzModalService,
    private dictionaryService: HxDictionaryService,
  ) {
  }

  ngOnInit() {
    const routeData: Data = this.aRoute.snapshot.data;
    this.dictionary = routeData['dictionary'] as DictionaryModel;
    this.aRoute.queryParamMap.pipe(takeUntil(this.$destroyed))
      .subscribe(paramMap => {
        this.params.query = paramMap.get('query') ?? undefined;
        this.loadEntries();
      });
  }

  applyFilters(delay?: number) {
    const navigateFn = () => this.router.navigate([], {
      relativeTo: this.aRoute,
      queryParams: toRouterQueryParams(this.params),
    });
    if (delay) {
      if (this.timeoutId) {
        clearTimeout(this.timeoutId);
      }
      this.timeoutId = setTimeout(() => navigateFn(), delay);
    } else {
      navigateFn();
    }
  }

  remove(code: string) {
    this.dictionaryService.removeEntryByCode(this.dictionary.key, code).then(() => {
      this.loadEntries();
      this.nt.success(this.tr.translate('nav.DictionaryEntries'), this.tr.translate('nt.dataRemoved'));
    });
  }

  protected loadList(): Promise<DictionaryEntryModel[]> {
    return this.dictionaryService.getEntries({
      key: this.dictionary.key as DictionaryKey,
      query: this.params.query
    });
  }

  startEdit(ix: number) {
    this.editIx = ix;
    this.dirty = true;
  }

  async saveEntries() {
    this.isLoading.saveEntries = true;

    const excludeRemovedFn = (items: DictionaryEntryItem[]) => {
      return items
        .filter(item => item.removed === undefined || !item.removed)
        .map(item => {
          delete item.removed;
          item.childEntries = excludeRemovedFn(item.childEntries);
          return item;
        });
    };

    try {
      await this.dictionaryService.saveEntries(this.dictionary.id, excludeRemovedFn(this.entries));
      this.dirty = false;
      this.editIx = undefined;
      this.nt.success(this.tr.translate('nav.DictionaryEntries'), this.tr.translate('nt.dataSaved'));
      await this.loadEntries();
    } finally {
      this.isLoading.saveEntries = false;
    }
  }

  async cancelModifications() {
    await this.loadEntries();
    this.dirty = false;
    this.editIx = undefined;
  }

  async showAddEntryModal() {
    const modalRef = this.modal.create({
      nzTitle: this.tr.translate('dictionaries.modal.addEntry.title') + ': ' + uiLabel(this.tr.getActiveLang(), this.dictionary.title),
      nzContent: AddEntryModal,
      nzFooter: null,
      nzData: {
        dictionary: this.dictionary,
      },
    });
    const res = await firstValueFrom(modalRef.afterClose);
    if (res === 'success') {
      await this.loadEntries();
    }
  }

  onBack() {
    return this.router.navigate(['dictionaries']);
  }

  updateAttributes(event: any, data: DictionaryEntryItem) {
    if (event) {
      try {
        data.attributes = JSON.parse(event);
      } catch (err: any) {
      }
    } else {
      data.attributes = undefined;
    }
  }

  toggleExpandAll(expandAll: boolean): void {
    this.expandAll = expandAll;
    this.entries.forEach(item => {
      const children = this.mapOfExpandedData[item.id];
      if (children) {
        children.forEach(child => child.expand = expandAll);
      }
    });

    this.entries.forEach(item => this.expandedEntries[item.id] = expandAll);
  }

  collapse(array: TreeNodeInterface[], data: TreeNodeInterface, $event: boolean): void {
    this.expandedEntries[data.id] = $event;
    if (!$event) {
      if (data.childEntries) {
        data.childEntries.forEach(d => {
          const target = array.find(a => a.id === d.id)!;
          target.expand = false;
          this.collapse(array, target, false);
        });
      } else {
        return;
      }
    }
  }

  onEntryChanged(updatedEntry: TreeNodeInterface) {
    if (this.timeoutId) {
      clearTimeout(this.timeoutId);
    }

    this.timeoutId = setTimeout(() => {
      const found = this.entryMap.get(updatedEntry.id);
      if (found) {
        const {level, expand, parent, removed, ...updatedEntryCopy} = updatedEntry;
        Object.assign(found, updatedEntryCopy);
      }
    }, 300);
  }

  attributeChange(event: any, data: DictionaryEntryItem) {
    this.updateAttributes(event, data);
    console.log('data=', data);
    this.onEntryChanged(data);
  }

  visitNode(node: TreeNodeInterface, hashMap: { [key: string]: boolean }, array: TreeNodeInterface[]): void {
    if (node.id) {
      if (!hashMap[node.id]) {
        hashMap[node.id] = true;
        array.push(node);
      }
    }
  }

  convertTreeToList(root: TreeNodeInterface): TreeNodeInterface[] {
    const stack: TreeNodeInterface[] = [];
    const array: TreeNodeInterface[] = [];
    const hashMap = {};
    stack.push({...root, level: 0, expand: false});

    while (stack.length !== 0) {
      const node = stack.pop()!;
      this.visitNode(node, hashMap, array);
      if (node.childEntries) {
        for (let i = node.childEntries.length - 1; i >= 0; i--) {
          stack.push({...node.childEntries[i], level: node.level! + 1, expand: false, parent: node});
        }
      }
    }

    return array;
  }

  async showAddChildEntryModal(data: DictionaryEntryModel) {
    const modalRef = this.modal.create({
      nzContent: AddEntryModal,
      nzFooter: null,
      nzData: {
        dictionary: this.dictionary,
        parentEntry: data,
      },
    });

    const res = await firstValueFrom(modalRef.afterClose);
    if (res === 'success') {
      this.nt.success('', 'nt.dataSaved');
      await this.loadEntries();
    }
  }

  async toggleRemoved(item: TreeNodeInterface) {
    const entry = this.entryMap.get(item.id);
    if (entry) {
      item.removed = !item.removed;
      entry.removed = item.removed;

      await this.dictionaryService.removeEntryByCode(this.dictionary.key, entry.code);
      this.loadEntries();
    }
    this.dirty = true;
  }

  private async loadEntries() {
    this.entries = await this.dictionaryService.getEntries({
      key: this.dictionary.key,
      query: this.params.query,
    });

    this.entries.forEach(item => {
      this.mapOfExpandedData[item.id] = this.convertTreeToList(item);
      if (this.expandedEntries[item.id]) {
        this.mapOfExpandedData[item.id].forEach(child => child.expand = true);
      }
    });

    this.mapEntries(this.entries);
  }

  private mapEntries(entries: DictionaryEntryModel[]) {
    entries.forEach(entry => {
      this.entryMap.set(entry.id, entry);
      this.mapEntries(entry.childEntries);
    });
  }
}
